/**
 * @fileoverview Business Performance page — Total Business 5-year trend.
 * @description Ported from the InsightBox Banking design system's own
 * `BusinessPerformance/TotalBusiness5Y.tsx` — structurally identical to
 * `BankingProfitabilityPage.tsx` (see that page's file comment), just tracking
 * `totalBusiness` (deposits + advances) instead of net profit.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useCurrentSession } from '@/features/auth'
import {
  BranchFilterSelect,
  FISCAL_YEARS,
  FIVE_YEAR_ID,
  FyFilterSelect,
  LastUpdatedLabel,
  TrendArea,
  branchLabel,
  fiveYearGrowth,
  formatCr,
  formatSignedPct,
  monthlySeries,
  peakYear,
  prevFY,
  resolveBankingAccessScope,
  resolveFy,
  sumAcrossYears,
  totalBusiness,
  troughYear,
  useBankingData,
  yoyGrowth,
} from '@/features/banking'
import BankingKpiStrip from '@/shared/components/molecules/BankingKpiStrip'
import BusinessSummaryPanel from '@/shared/components/molecules/BusinessSummaryPanel'
import ChartCard from '@/shared/components/molecules/ChartCard'
import PageHeader from '@/shared/components/molecules/PageHeader'

import type { FyFilter } from '@/features/banking'
import type { BankingKpiStripItem } from '@/shared/components/molecules/BankingKpiStrip'

/**
 * @component
 */
function BankingBusinessPerformancePage() {
  const { t } = useTranslation()
  const dataQuery = useBankingData()
  const [fyFilter, setFyFilter] = useState<FyFilter>(FIVE_YEAR_ID)
  const bankingScope = resolveBankingAccessScope(useCurrentSession().data?.bankingScope)
  const [branchId, setBranchId] = useState(bankingScope.defaultBranchId)

  const header = (
    <PageHeader
      kicker={t('banking.businessPerformance.kicker')}
      title={t('banking.businessPerformance.title')}
      description={t('banking.businessPerformance.description')}
      filters={
        <>
          <FyFilterSelect value={fyFilter} onChange={setFyFilter} />
          <BranchFilterSelect value={branchId} onChange={setBranchId} scope={bankingScope} />
          {dataQuery.data && <LastUpdatedLabel value={dataQuery.data.meta.lastUpdated} />}
        </>
      }
    />
  )

  if (dataQuery.isPending) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <BankingKpiStrip
          loading
          items={[
            { label: '', value: '', highlight: true },
            { label: '', value: '' },
            { label: '', value: '' },
            { label: '', value: '' },
          ]}
        />
        <ChartCard title="" loading>
          <div />
        </ChartCard>
      </div>
    )
  }

  if (dataQuery.isError) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <p className="text-sm text-danger">{t('banking.loadError')}</p>
      </div>
    )
  }

  const data = dataQuery.data
  const is5Years = fyFilter === FIVE_YEAR_ID
  const fy = resolveFy(fyFilter)
  const selector = (f: (typeof FISCAL_YEARS)[number]) => totalBusiness(data, f, branchId)

  const current = selector(fy)
  const yoy = yoyGrowth(fy, selector)
  const priorFy = prevFY(fy)
  const priorValue = priorFy ? selector(priorFy) : null
  const fiveYear = fiveYearGrowth(selector)
  const peak = peakYear(selector)
  const trough = troughYear(selector)
  const sumBusiness = sumAcrossYears(selector)

  const chartData = is5Years
    ? FISCAL_YEARS.map((f) => ({ fy: f, value: selector(f), growthPct: yoyGrowth(f, selector) }))
    : monthlySeries(data, fy, selector)

  const kpis: BankingKpiStripItem[] = is5Years
    ? [
        {
          label: t('banking.businessPerformance.kpi.fiveYearSum'),
          value: formatCr(sumBusiness),
          sublabel: t('banking.businessPerformance.kpi.fiveYearSumSublabel'),
          highlight: true,
        },
        {
          label: t('banking.businessPerformance.kpi.latestYear', { fy }),
          value: formatCr(current),
          sublabel: t('banking.businessPerformance.kpi.latestYearSublabel'),
        },
        {
          label: t('banking.businessPerformance.kpi.fiveYearGrowth'),
          value: formatSignedPct(fiveYear),
          sublabel: t('banking.businessPerformance.kpi.fiveYearGrowthSublabel'),
        },
        {
          label: t('banking.businessPerformance.kpi.peak'),
          value: formatCr(peak.value),
          sublabel: peak.fy,
        },
      ]
    : [
        {
          label: t('banking.businessPerformance.kpi.totalBusiness'),
          value: formatCr(current),
          sublabel: fy,
          highlight: true,
        },
        {
          label: t('banking.businessPerformance.kpi.priorYear'),
          value: priorValue !== null ? formatCr(priorValue) : '—',
          sublabel: priorFy ?? t('banking.businessPerformance.kpi.noPriorYear'),
        },
        {
          label: t('banking.businessPerformance.kpi.yoyGrowth'),
          value: yoy !== null ? formatSignedPct(yoy) : '—',
          sublabel: yoy !== null ? t('banking.businessPerformance.kpi.vsPriorYear') : '',
        },
        {
          label: t('banking.businessPerformance.kpi.fiveYearPeak'),
          value: formatCr(peak.value),
          sublabel: peak.fy,
        },
      ]

  return (
    <div className="flex flex-col gap-4">
      {header}
      <BankingKpiStrip items={kpis} />

      <ChartCard
        fill
        title={
          is5Years
            ? t('banking.businessPerformance.chartTitleFiveYear')
            : t('banking.businessPerformance.chartTitleMonthly', { fy })
        }
        subtitle={
          is5Years
            ? t('banking.businessPerformance.chartSubtitleFiveYear', {
                branch: branchLabel(branchId),
              })
            : t('banking.businessPerformance.chartSubtitleMonthly', {
                branch: branchLabel(branchId),
              })
        }
      >
        <div className="relative min-h-0 flex-1">
          <TrendArea
            key={is5Years ? '5y' : 'monthly'}
            data={chartData}
            formatValue={(v) => formatCr(v)}
            height="100%"
            colorByTrend
          />
        </div>
      </ChartCard>

      <BusinessSummaryPanel
        title={t('banking.businessSummary')}
        lines={
          is5Years
            ? [
                t('banking.businessPerformance.summary.fiveYearLine1', {
                  current: formatCr(current),
                  fy,
                  yoyClause:
                    yoy !== null
                      ? t('banking.businessPerformance.summary.yoyClause', {
                          value: formatSignedPct(yoy),
                        })
                      : '',
                  sum: formatCr(sumBusiness),
                }),
                t('banking.businessPerformance.summary.fiveYearLine2', {
                  troughValue: formatCr(trough.value),
                  troughFy: trough.fy,
                  peakValue: formatCr(peak.value),
                  peakFy: peak.fy,
                  growth: formatSignedPct(fiveYear),
                }),
              ]
            : [
                t('banking.businessPerformance.summary.yearLine1', {
                  current: formatCr(current),
                  fy,
                  yoyClause:
                    yoy !== null
                      ? t('banking.businessPerformance.summary.yoyClauseYear', {
                          direction: yoy >= 0 ? t('banking.up') : t('banking.down'),
                          value: formatSignedPct(yoy),
                          priorFy,
                        })
                      : '',
                }),
                t('banking.businessPerformance.summary.yearLine2'),
              ]
        }
      />
    </div>
  )
}

export default BankingBusinessPerformancePage
